import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { formatDistanceToNow } from 'date-fns';
import { Activity, Archive, ArchiveRestore, Clock, Phone, Mail, Calendar, FileText, MessageSquare, AlertTriangle } from 'lucide-react';

interface ActivitySummary {
  activityCount30Days: number;
  lastActivityAt: string | null;
  lastActivityType: string | null;
}

interface RecentActivityCardProps {
  activitySummary: ActivitySummary;
  contractDaysRemaining?: number | null;
  hasOpenDeals?: boolean;
  onNavigateToTab: (tab: string) => void;
}

export default function RecentActivityCard({
  activitySummary,
  contractDaysRemaining,
  hasOpenDeals,
  onNavigateToTab,
}: RecentActivityCardProps) {
  const { activityCount30Days, lastActivityAt, lastActivityType } = activitySummary;

  // Calculate engagement level
  const engagementLevel = getEngagementLevel(activityCount30Days);

  // Suggest next action based on context
  const suggestedAction = getSuggestedAction({
    lastActivityAt,
    lastActivityType,
    contractDaysRemaining,
    hasOpenDeals,
  });

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2">
          <Activity className="h-4 w-4" />
          Recent Activity
        </CardTitle>
        <CardDescription>Engagement and next steps</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Last Contact */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Clock className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Last contact</span>
          </div>
          <div className="flex items-center gap-2">
            {lastActivityAt ? (
              <>
                {getActivityIcon(lastActivityType)}
                <span className="text-sm font-medium">
                  {formatDistanceToNow(new Date(lastActivityAt), { addSuffix: true })}
                </span>
              </>
            ) : (
              <span className="text-sm text-muted-foreground">No recent activity</span>
            )}
          </div>
        </div>

        {/* Activity Count */}
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Last 30 days</span>
          <div className="flex items-center gap-2">
            <Badge
              variant="outline"
              className={`${engagementLevel.color} ${engagementLevel.bgColor}`}
            >
              {activityCount30Days} {activityCount30Days === 1 ? 'activity' : 'activities'}
            </Badge>
            <span className={`text-xs font-medium ${engagementLevel.color}`}>
              {engagementLevel.label}
            </span>
          </div>
        </div>

        {/* Suggested Next Action */}
        {suggestedAction && (
          <div className="pt-2 border-t">
            <p className="text-xs text-muted-foreground mb-1">Suggested next action</p>
            <div className="flex items-center gap-2 p-2 rounded-md bg-blue-50 text-blue-700">
              {suggestedAction.icon}
              <span className="text-sm font-medium">{suggestedAction.message}</span>
            </div>
          </div>
        )}

        {/* View All Link */}
        <button
          onClick={() => onNavigateToTab('activity')}
          className="text-xs text-primary hover:underline"
        >
          View all activity &rarr;
        </button>
      </CardContent>
    </Card>
  );
}

function getEngagementLevel(count: number): {
  label: string;
  color: string;
  bgColor: string;
} {
  if (count >= 10) {
    return { label: 'High', color: 'text-green-600', bgColor: 'bg-green-50 border-green-200' };
  }
  if (count >= 5) {
    return { label: 'Medium', color: 'text-yellow-600', bgColor: 'bg-yellow-50 border-yellow-200' };
  }
  if (count >= 1) {
    return { label: 'Low', color: 'text-orange-600', bgColor: 'bg-orange-50 border-orange-200' };
  }
  return { label: 'None', color: 'text-red-600', bgColor: 'bg-red-50 border-red-200' };
}

function getActivityIcon(activityType: string | null) {
  switch (activityType) {
    case 'call':
    case 'phone':
      return <Phone className="h-3.5 w-3.5 text-green-600" />;
    case 'email':
      return <Mail className="h-3.5 w-3.5 text-blue-600" />;
    case 'meeting':
      return <Calendar className="h-3.5 w-3.5 text-purple-600" />;
    case 'note':
    case 'document':
      return <FileText className="h-3.5 w-3.5 text-gray-600" />;
    case 'message':
      return <MessageSquare className="h-3.5 w-3.5 text-orange-600" />;
    case 'account_archived':
      return <Archive className="h-3.5 w-3.5 text-amber-600" />;
    case 'account_unarchived':
      return <ArchiveRestore className="h-3.5 w-3.5 text-emerald-600" />;
    default:
      return <Activity className="h-3.5 w-3.5 text-muted-foreground" />;
  }
}

interface SuggestedActionContext {
  lastActivityAt: string | null;
  lastActivityType: string | null;
  contractDaysRemaining?: number | null;
  hasOpenDeals?: boolean;
}

function getSuggestedAction(context: SuggestedActionContext): {
  message: string;
  icon: React.ReactNode;
} | null {
  const { lastActivityAt, contractDaysRemaining, hasOpenDeals } = context;

  // Check if contract is expiring soon
  if (contractDaysRemaining !== undefined && contractDaysRemaining !== null && contractDaysRemaining <= 60) {
    return {
      message: 'Schedule renewal discussion',
      icon: <Calendar className="h-3.5 w-3.5" />,
    };
  }

  // Check if no recent activity
  if (!lastActivityAt) {
    return {
      message: 'Reach out to establish contact',
      icon: <Phone className="h-3.5 w-3.5" />,
    };
  }

  const daysSinceLastActivity = Math.floor(
    (Date.now() - new Date(lastActivityAt).getTime()) / (1000 * 60 * 60 * 24)
  );

  // If no activity in 14+ days and has open deals
  if (daysSinceLastActivity >= 14 && hasOpenDeals) {
    return {
      message: 'Follow up on open deals',
      icon: <AlertTriangle className="h-3.5 w-3.5" />,
    };
  }

  // If no activity in 30+ days
  if (daysSinceLastActivity >= 30) {
    return {
      message: 'Schedule check-in call',
      icon: <Phone className="h-3.5 w-3.5" />,
    };
  }

  // If no activity in 7+ days
  if (daysSinceLastActivity >= 7) {
    return {
      message: 'Send follow-up email',
      icon: <Mail className="h-3.5 w-3.5" />,
    };
  }

  return null;
}
